/**
 * Sticky Facets Class Handler
 * Adds a class to the facets container when it becomes sticky
 */

class StickyFacetsClassHandler {
  constructor() {
    this.facetsContainer = null;
    this.originalTop = 0;
    this.scrollHandler = null;
    this.mutationObserver = null;
    this.isSticky = false;
    this.observer = null;
    this.init();
  }

  init() {
    // Set up MutationObserver to watch for GrapheneHC DOM changes
    this.setupMutationObserver();
    
    // Initial setup
    this.setupStickyClassDetection();
    
    // Also listen for collection-ready as backup
    document.addEventListener('collection-ready', () => {
      setTimeout(() => {
        this.ensureStickyState();
      }, 50);
    });

    // Fallback listeners
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        if (!this.facetsContainer) {
          this.setupStickyClassDetection();
        }
      }, 1000);
    });
  }

  setupMutationObserver() {
    // Watch for changes in the main content area where GrapheneHC operates
    const targetNode = document.querySelector('main') || document.body;
    
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if facets-related nodes were added
          const addedNodes = Array.from(mutation.addedNodes);
          const hasFacetsContent = addedNodes.some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.querySelector && node.querySelector('.facets__bar') || 
             node.classList && node.classList.contains('facets__bar'))
          );
          
          if (hasFacetsContent) {
            // Small delay to let the DOM settle
            setTimeout(() => {
              this.ensureStickyState();
            }, 100);
          }
        }
      });
    });

    this.mutationObserver.observe(targetNode, {
      childList: true,
      subtree: true
    });
  }

  // Quick method to ensure sticky state AND scroll handler
  ensureStickyState() {
    // Get fresh reference to container (in case it was recreated)
    const currentContainer = document.querySelector('.facets:has(.facets__bar)');
    
    if (!currentContainer) {
      return;
    }

    // Update our reference if it changed
    if (currentContainer !== this.facetsContainer) {
      // Container changed, need full setup to get proper scroll handler
      this.facetsContainer = currentContainer;
      this.setupStickyClassDetection();
      return;
    }

    // If we don't have a scroll handler, set up the full detection
    if (!this.scrollHandler) {
      this.setupStickyClassDetection();
      return;
    }

    // Quick state check for existing setup
    const scrollTop = window.scrollY;
    const stickyThreshold = 65;
    
    // Use stored originalTop if we have it
    if (this.originalTop <= 0) {
      this.setupStickyClassDetection();
      return;
    }

    const shouldBeSticky = scrollTop >= (this.originalTop - stickyThreshold);

    // Apply or remove the class as needed
    if (shouldBeSticky && !this.facetsContainer.classList.contains('is-stuck')) {
      this.facetsContainer.classList.add('is-stuck');
      this.isSticky = true;
    } else if (!shouldBeSticky && this.facetsContainer.classList.contains('is-stuck')) {
      this.facetsContainer.classList.remove('is-stuck');
      this.isSticky = false;
    }
  }

  setupStickyClassDetection() {
    // Clean up any existing setup first
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    // Reset sticky state since we're re-setting up
    this.isSticky = false;
    
    // Find the sticky container (fresh reference after GrapheneHC recreation)
    this.facetsContainer = document.querySelector('.facets:has(.facets__bar)');
    
    if (!this.facetsContainer) {
      return;
    }

    // Make sure the facets bar actually exists
    const facetsBar = this.facetsContainer.querySelector('.facets__bar');
    if (!facetsBar) {
      return;
    }

    // Set up click handlers for facet chips
    this.setupFacetChipClickHandlers();

    // Get the original position of the facets container
    // Wait a moment for any animations or layout changes to complete
    setTimeout(() => {
      // Store current scroll position before any manipulation
      const currentScrollY = window.scrollY;
      
      // To get the true original position, we need to temporarily disable sticky positioning
      const computedStyle = window.getComputedStyle(this.facetsContainer);
      const originalPosition = computedStyle.position;
      const originalTopStyle = computedStyle.top;
      
      // Temporarily disable sticky to get natural position
      this.facetsContainer.style.position = 'static';
      this.facetsContainer.style.top = 'auto';
      
      // Force reflow and get the natural position
      this.facetsContainer.offsetHeight; // Force reflow
      const rect = this.facetsContainer.getBoundingClientRect();
      const naturalTop = rect.top + window.scrollY;
      
      // Restore original positioning
      this.facetsContainer.style.position = originalPosition;
      this.facetsContainer.style.top = originalTopStyle;
      
      // Use the natural position for our calculations
      this.originalTop = naturalTop;
      const stickyThreshold = 65; // Should match the CSS top: 65px value

      // Use scroll event for more reliable sticky detection
      const handleScroll = () => {
        if (!this.facetsContainer) return;
        
        const scrollTop = window.scrollY;
        const shouldBeSticky = scrollTop >= (this.originalTop - stickyThreshold);
        
        if (shouldBeSticky && !this.isSticky) {
          this.addStickyClass();
        } else if (!shouldBeSticky && this.isSticky) {
          this.removeStickyClass();
        }
      };

      // Add scroll listener
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Store the handler for cleanup
      this.scrollHandler = handleScroll;
      
      // Do an initial check - important for pages that load while scrolled
      const initialScrollTop = window.scrollY;
      const shouldInitiallyBeSticky = initialScrollTop >= (this.originalTop - stickyThreshold);
      
      if (shouldInitiallyBeSticky) {
        this.addStickyClass();
      }
      
    }, 100); // Small delay to ensure layout is stable
  }

  setupFacetChipClickHandlers() {
    if (!this.facetsContainer) return;

    // Find all facet chips
    const facetChips = this.facetsContainer.querySelectorAll('.facet-chip');
    
    facetChips.forEach(chip => {
      // Remove any existing click handler to avoid duplicates
      chip.removeEventListener('click', this.handleFacetChipClick);
      
      // Add click handler
      chip.addEventListener('click', (e) => this.handleFacetChipClick(e));
    });
  }

  handleFacetChipClick(e) {

    // e.preventDefault();
    // Allow the default click behavior first (the filtering)
    // But add our scroll behavior after a short delay to let GrapheneHC do its work
    setTimeout(() => {
      this.scrollToFilterBarPosition();
    }, 100);
  }

  scrollToFilterBarPosition() {
    if (!this.facetsContainer || this.originalTop <= 0) {
      return;
    }

    // Calculate the position to scroll to
    // We want to scroll to where the filter bar naturally sits, minus a small offset
    const targetScrollPosition = this.originalTop - 50; // 80px offset from top for breathing room
    const maxScroll = Math.max(0, targetScrollPosition); // Don't scroll above top of page

    // Smooth scroll to the position
    window.scrollTo({
      top: maxScroll,
      behavior: 'smooth'
    });
  }

  addStickyClass() {
    if (!this.isSticky && this.facetsContainer) {
      this.facetsContainer.classList.add('is-stuck');
      this.isSticky = true;
      
     // Dispatch custom event
      this.facetsContainer.dispatchEvent(new CustomEvent('facets-stuck', {
        bubbles: true,
        detail: { stuck: true }
      }));
    }
  }

  removeStickyClass() {
    if (this.isSticky && this.facetsContainer) {
      this.facetsContainer.classList.remove('is-stuck');
      this.isSticky = false;
      
      // Dispatch custom event
      this.facetsContainer.dispatchEvent(new CustomEvent('facets-unstuck', {
        bubbles: true,
        detail: { stuck: false }
      }));
    }
  }

  // Method to check current state without full recalculation
  checkAndUpdateStickyState() {
    if (!this.facetsContainer || !this.scrollHandler || this.originalTop <= 0) {
      return;
    }

    const scrollTop = window.scrollY;
    const stickyThreshold = 65;
    const shouldBeSticky = scrollTop >= (this.originalTop - stickyThreshold);

    if (shouldBeSticky && !this.isSticky) {
      this.addStickyClass();
    } else if (!shouldBeSticky && this.isSticky) {
      this.removeStickyClass();
    }
  }

  // Method to manually recalculate
  recalculate() {
    // Clean up existing listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Reset state
    this.isSticky = false;
    if (this.facetsContainer) {
      this.facetsContainer.classList.remove('is-stuck');
    }
    
    // Re-setup the detection
    this.setupStickyClassDetection();
  }

  // Clean up
  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.facetsContainer) {
      this.facetsContainer.classList.remove('is-stuck');
    }
    // Remove any old sentinel elements
    const oldSentinels = document.querySelectorAll('.sticky-sentinel');
    oldSentinels.forEach(sentinel => sentinel.remove());
  }
}

// Initialize
const stickyFacetsHandler = new StickyFacetsClassHandler();

// Export for external use
window.stickyFacets = stickyFacetsHandler;
